import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import Button from '@/components/CustomButtons/Button';
import { CircularProgress } from '@mui/material';
import { AudienceApi } from '@/api/clients/audience/audience.api';
import { PUBLIC_ASSETS_BASE_URL } from '@/config';
import BusinessUnitLogoPlaceholder from '@/assets/img/BusinessUnitLogoPlaceholder.png?inline';
import AudienceTemplates from './AudienceTemplates';
import AudienceCard from './AudienceCard';

export const TemplateTypes = Object.freeze({
  OTP: 'Otp',
  PAYMENT: 'Payment',
  REMINDER_1: 'Reminder-1',
  REMINDER_2: 'Reminder-2',
  CANCEL: 'Cancel',
  CONFIRMATION: 'Confirmation',
  LETTER: 'Letter',
  E_CONSENT: 'E-Consent',
  ACH_RETURN_NOTIFICATION_FOR_EMAIL: 'ach_return_notification_for_email',
  ACH_RETURN_NOTIFICATION_FOR_LETTER: 'ach_return_notification_for_letter',
  SECURE_LINK: 'Secure-Link',
  CLAIM_APPROVED: 'Claim-Approved',
  REFUND_PROCESSED: 'Refund-Processed',
  REFUND_CANCELLED: 'Refund-Cancelled',
});

const templatesDefaultValues = [
  {
    key: 'otp',
    type: TemplateTypes.OTP,
    subject: 'Account verification',
    heading: 'Account verification',
    cc: '',
    body: `To verify your email address, please use the following One Time Password (OTP): {{otp}}
  
  Do not share this OTP with anyone. Client Pay Direct takes your account security very seriously. Client Pay Direct will never ask you to disclose or verify your OTP, credit card, or banking account number. If you receive a suspicious email with a link to update your account information, do not click on the link—instead, report the email to Client Pay Direct for investigation.
    
  Thank you!`,
  },
  {
    key: 'refund',
    type: TemplateTypes.PAYMENT,
    subject: 'Your refund from {{facility_name}}.',
    heading: 'Dear {{refund_name}},',
    cc: '',
    body: `We are writing to inform you that {{facility_name}} owes you money in the amount of {{refund_amount}}.{{get_started_button}} Please click on the link below to authenticate yourself, and process the payment. If you have any questions, please reach out to {{facility_name}} at (facility number / email).`,
  },
  {
    key: 'reminder',
    type: TemplateTypes.REMINDER_1,
    subject: 'Reminder for refund payment.',
    heading: 'Dear {{refund_name}},',
    cc: '',
    body:
      'This is reminder to claim you refund from . Please click below and you can get your refund in 2 easy steps.{{get_started_button}}',
  },
  {
    key: 'reminder2',
    type: TemplateTypes.REMINDER_2,
    subject: 'Reminder for refund payment.',
    heading: 'Dear {{refund_name}},',
    cc: '',
    body:
      'This is reminder to claim you refund from . Please click below and you can get your refund in 2 easy steps.get_started_button',
  },
  {
    key: 'thankyou',
    type: TemplateTypes.CONFIRMATION,
    subject: 'Payment Confirmation',
    heading: 'Your refund has been processed!',
    cc: '',
    body:
      'Thank you for using Client Pay Direct. Your funds have been sent to you via {{payment_method}}.',
  },
  {
    key: 'cancel',
    type: TemplateTypes.CANCEL,
    subject: 'Refund Canceled',
    heading: 'Dear {{refund_name}},',
    cc: '',
    body: 'Your refund has been canceled.',
  },
  {
    key: 'letter',
    type: TemplateTypes.LETTER,
    name: 'Test',
    date: 'date',
    signature: 'Test',
    body: `Dear {{refund_name}},
  We are writing to inform you that {{facility_name}} owes you money in the amount of {{refund_amount}}. Please click on the link below to authenticate yourself, and process the payment. If you have any questions, please reach out to {{facility_name}} at (facility number / email).`,
  },
  {
    key: 'econsent',
    type: TemplateTypes.E_CONSENT,
    name: 'E-Consent',
    subject: '',
    heading: '',
    body: ``,
    enabled: false,
  },
  {
    key: 'ach_return_notification_for_email',
    type: TemplateTypes.ACH_RETURN_NOTIFICATION_FOR_EMAIL,
    subject: 'ACH Return Notification',
    heading: 'Dear {{first_name}} {{last_name}},',
    cc: '',
    body: `Your claim of {{refund_amount}} from {{facility_name}} was not able to be processed.

Return Details:
- Return Code: {{return_code}}
- Return Reason: {{return_description}}
- Tracer Number: {{tracer_number}}
- Account (Last 4): {{last_4_account}}

Click the link below to restart your claim process. 

{{get_started_button}}

Thanks,
Client Pay Direct
on behalf of {{facility_name}}`,
  },
  {
    key: 'ach_return_notification_for_letter',
    type: TemplateTypes.ACH_RETURN_NOTIFICATION_FOR_LETTER,
    subject: 'ACH Return Notification',
    heading: 'Dear {{first_name}} {{last_name}},',
    cc: '',
    body: `Your claim of {{refund_amount}} from {{facility_name}} was not able to be processed.

Return Details:
- Return Code: {{return_code}}
- Return Reason: {{return_description}}
- Tracer Number: {{tracer_number}}
- Account (Last 4): {{last_4_account}}

Please visit {{claim_url}} and enter {{unique_code}} to restart your claim process.

Thanks,
Client Pay Direct
on behalf of {{facility_name}}`,
  },
  {
    key: 'secure_link',
    type: TemplateTypes.SECURE_LINK,
    subject: '{{facility_name}} - Your secure link is here!',
    heading: 'Hi {{refund_name}} 👋,<br>Your secure link is here!',
    cc: '',
    body: `We're reaching out from TailFin Payments on behalf of {{facility_name}}.

To quickly sign into our platform, click the secure link below.

This link will expire in 1 hour.

{{secure_button}}

Please don't share your link.

{{security_warning}}

Thanks, TailFin Payments on behalf of {{facility_name}}.`,
  },
  {
    key: 'claim_approved',
    type: TemplateTypes.CLAIM_APPROVED,
    subject: 'Your Claim Has Been Approved',
    heading: 'Hi {{refund_name}} 👋,<br>Your claim has been approved!',
    cc: '',
    body: `We're reaching out from TailFin Payments on behalf of {{facility_name}} with some great news: your claim has been approved!

{{claim_info_boxes}}

{{payment_methods}}

To get started, click the link below. Please don't share it.

{{claim_button}}

If you have any questions about your claim or the payment process, feel free to call us at {{phone_number}}.

Thanks, TailFin Payments on behalf of {{facility_name}}.`,
  },
  {
    key: 'refund_processed',
    type: TemplateTypes.REFUND_PROCESSED,
    subject: 'Your Refund Has Been Processed',
    heading: 'Hi {{refund_name}} 👋,<br>Your refund has been processed!',
    cc: '',
    body: `We're reaching out from TailFin Payments on behalf of {{facility_name}} with some great news: your refund has been processed!

Your funds will be deposited into your account in 3-5 business days.

{{download_confirmation}}

Thanks, TailFin Payments on behalf of {{facility_name}}.`,
  },
  {
    key: 'refund_cancelled',
    type: TemplateTypes.REFUND_CANCELLED,
    subject: 'Your Refund Has Been Canceled',
    heading: 'Hi {{refund_name}} 👋,<br>The refund has been canceled.',
    cc: '',
    body: `We're reaching out from TailFin Payments on behalf of {{facility_name}}. This refund has been canceled.

For further information, please reach out using the contact information provided in the original refund notification.

Thanks, TailFin Payments on behalf of {{facility_name}}.`,
  },
];

export default function CreateOrEditAudience() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isCreate = !id;
  const [isEditingTemplates, setIsEditingTemplates] = useState(false);
  const [isEditingAudience, setIsEditingAudience] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    audience_identifiers: [],
    logo: '',
    logoPreview: null,
    include_cpd_logo: false,
    use_business_unit_logo: false,
    email_domain_id: '0',
    email_local_part: 'postmaster',
  });
  const [templates, setTemplates] = useState(templatesDefaultValues);
  const [selectedTemplate, setSelectedTemplate] = useState(TemplateTypes.OTP);

  useEffect(() => {
    if (id) {
      fetchAudience();
    }
  }, [id]);

  const fetchAudience = async () => {
    setLoading(true);
    try {
      const audience = await AudienceApi.getAudience(id);

      setFormData({
        name: audience.name || '',
        audience_identifiers:
          audience.audience_identifiers || audience.identifiers || [],
        logo: audience.logo || '',
        logoPreview: audience.use_business_unit_logo
          ? BusinessUnitLogoPlaceholder
          : audience.logo
          ? PUBLIC_ASSETS_BASE_URL + audience.logo
          : null,
        include_cpd_logo: audience.include_cpd_logo || false,
        use_business_unit_logo: audience.use_business_unit_logo || false,
        email_domain_id: audience.email_domain_id || '0',
        email_local_part: audience.email_sender_local_part || 'postmaster',
      });

      if (
        audience.templates &&
        Array.isArray(audience.templates) &&
        audience.templates.length > 0
      ) {
        const audienceTemplates = audience.templates.map((template) => ({
          key: template.type.toLowerCase(),
          type: template.type,
          subject: template.subject || '',
          heading: template.heading || '',
          body: template.body || '',
          cc: template.cc || '',
          bcc: template.bcc || '',
          name: template.name || '',
          date: template.date || '',
          signature: template.signature || '',
        }));

        const templateTypes = audienceTemplates.map((t) => t.type);

        const missingTemplates = templatesDefaultValues.filter(
          (template) => !templateTypes.includes(template.type),
        );

        setTemplates([...audienceTemplates, ...missingTemplates]);

        if (audienceTemplates.length > 0) {
          setSelectedTemplate(audienceTemplates[0].type);
        }
      }

      setIsEditingTemplates(false);
      setIsEditingAudience(false);
    } catch (error) {
      console.error('Error fetching audience:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    setFormData({
      ...formData,
      logo: file,
      logoPreview: URL.createObjectURL(file),
      use_business_unit_logo: false,
    });
  };

  const clearLogo = () => {
    setFormData({
      ...formData,
      logo: '',
      logoPreview: null,
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'use_business_unit_logo') {
      setFormData({
        ...formData,
        use_business_unit_logo: checked,
        logo: '',
        logoPreview: checked ? BusinessUnitLogoPlaceholder : null,
      });
    } else if (name === 'include_cpd_logo') {
      setFormData({
        ...formData,
        include_cpd_logo: checked,
      });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    const formPayload = new FormData();
    formPayload.append('name', formData.name);
    formPayload.append(
      'audience_identifiers',
      JSON.stringify(formData.audience_identifiers),
    );
    if (formData.logo) {
      formPayload.append('logo', formData.logo);
    }
    formPayload.append('include_cpd_logo', formData.include_cpd_logo);
    formPayload.append(
      'use_business_unit_logo',
      formData.use_business_unit_logo,
    );

    if (formData.email_domain_id) {
      formPayload.append('email_domain_id', formData.email_domain_id);
    }
    if (formData.email_local_part) {
      formPayload.append('email_sender_local_part', formData.email_local_part);
    }

    formPayload.append('templates', JSON.stringify(templates));

    try {
      if (id) {
        await AudienceApi.updateAudience(id, formPayload);
      } else {
        await AudienceApi.createAudience(formPayload);
      }
      navigate('/facility/audience');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateUpdate = async () => {
    setLoading(true);
    try {
      await AudienceApi.updateAudienceTemplates(id, {
        templates: JSON.stringify(templates),
      });
      setIsEditingTemplates(false);
    } catch (error) {
      console.error('Error updating templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAudienceUpdate = async () => {
    setLoading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append(
        'audience_identifiers',
        JSON.stringify(formData.audience_identifiers),
      );
      if (formData.logo) {
        formPayload.append('logo', formData.logo);
      }
      formPayload.append('include_cpd_logo', formData.include_cpd_logo);
      formPayload.append(
        'use_business_unit_logo',
        formData.use_business_unit_logo,
      );

      if (formData.email_domain_id) {
        formPayload.append('email_domain_id', formData.email_domain_id);
      }
      if (formData.email_local_part) {
        formPayload.append(
          'email_sender_local_part',
          formData.email_local_part,
        );
      }

      await AudienceApi.updateAudience(id, formPayload);
      setIsEditingAudience(false);
    } catch (error) {
      console.error('Error updating audience details:', error);
    } finally {
      setLoading(false);
    }
  };

  const addIdentifier = () => {
    setFormData({
      ...formData,
      audience_identifiers: [...formData.audience_identifiers, ''],
    });
  };

  const onIdentifierChange = (e, index) => {
    const newIdentifiers = formData.audience_identifiers.map((identifier, i) =>
      i === index ? e.target.value : identifier,
    );
    setFormData({
      ...formData,
      audience_identifiers: newIdentifiers,
    });
  };

  const removeIdentifier = (index) => {
    const newIdentifiers = formData.audience_identifiers.filter(
      (identifier, i) => i !== index,
    );
    setFormData({
      ...formData,
      audience_identifiers: newIdentifiers,
    });
  };

  const handleTemplateChanges = (e, templateType, field) => {
    const { value } = e.target;
    setTemplates((prev) =>
      prev.map((templateItem) =>
        templateItem.type === templateType
          ? { ...templateItem, [field]: value }
          : templateItem,
      ),
    );
  };

  const handleTemplateChange = (e) => {
    setSelectedTemplate(e.target.value);
  };

  return (
    <GridContainer>
      <AudienceCard
        formData={formData}
        handleChange={handleChange}
        handleLogoUpload={handleLogoUpload}
        clearLogo={clearLogo}
        handleCheckboxChange={handleCheckboxChange}
        addIdentifier={addIdentifier}
        onIdentifierChange={onIdentifierChange}
        removeIdentifier={removeIdentifier}
        isCreate={isCreate}
        isEditingAudience={isEditingAudience}
        setIsEditingAudience={setIsEditingAudience}
        handleSubmit={isCreate ? handleSubmit : handleAudienceUpdate}
        loading={loading}
        navigate={navigate}
        setFormData={setFormData}
      />
      <AudienceTemplates
        templates={templates}
        selectedTemplate={selectedTemplate}
        handleTemplateChange={handleTemplateChange}
        handleTemplateChanges={handleTemplateChanges}
        isCreate={isCreate}
        isEditingTemplates={isEditingTemplates}
        setIsEditingTemplates={setIsEditingTemplates}
        handleSubmit={isCreate ? handleSubmit : handleTemplateUpdate}
        loading={loading}
        setTemplates={setTemplates}
        audienceId={id}
        logoData={{
          logo: formData.logo,
          include_cpd_logo: formData.include_cpd_logo,
          use_business_unit_logo: formData.use_business_unit_logo,
        }}
      />
      {isCreate && (
        <div style={{ textAlign: 'right', marginTop: '20px' }}>
          <Button
            color='rose'
            onClick={() => navigate('/facility/audience')}
            style={{ marginRight: '10px' }}>
            {t('common.buttons.cancel')}
          </Button>
          <Button color='primary' onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress
                  size={20}
                  color='inherit'
                  style={{ marginRight: '8px' }}
                />
                <span>{t('common.saving')}</span>
              </div>
            ) : (
              t('common.buttons.save')
            )}
          </Button>
        </div>
      )}
    </GridContainer>
  );
}
